import classNames from 'classnames';
import { type JSX, useCallback, useMemo, useState } from 'react';
import { type IntlShape, useIntl } from 'react-intl';
import ReviewFilterAction from '@alltrails/amplitude/enums/ReviewFilterAction';
import { IconProps } from '@alltrails/denali/components/Icon';
import ArrowSq from '@alltrails/denali/icons/ArrowSq';
import { useLocalStorage } from '@alltrails/local-storage';
import FullscreenSearchBox from '@alltrails/search/components/FullscreenSearchBox';
import type { OnSelect, SuggestionResult } from '@alltrails/search/types/searchBoxTypes';
import { useAppDispatch, useAppSelector } from '../../../redux';
import { saveInterstitial, setInterstitialQuery } from '../../../slices/reviewsSearchFilters';
import getReviewFilterCount from '../../../utils/getReviewFilterCount';
import FilterButton from '../FilterButton/FilterButton';
import ReviewSearchSuggestion from './ReviewSearchSuggestion';
import styles from './ReviewsSearchBox.module.scss';

const REVIEWS_SEARCH_HISTORY_KEY = 'reviews-search-history';

function getSearchSuggestions(intl: IntlShape) {
  return [
    { id: 'dogs', suggestionText: intl.formatMessage({ defaultMessage: 'Dogs' }) },
    { id: 'busy', suggestionText: intl.formatMessage({ defaultMessage: 'Busy' }) },
    { id: 'parking', suggestionText: intl.formatMessage({ defaultMessage: 'Parking' }) },
    { id: 'views', suggestionText: intl.formatMessage({ defaultMessage: 'Views' }) }
  ];
}

type ReviewsSearchBoxProps = {
  className?: string;
  onFilterClick: () => void;
  onSearch: (action?: ReviewFilterAction) => void;
  onSearchFocus?: () => void;
};

const ReviewsSearchBox = ({ className, onFilterClick, onSearch, onSearchFocus }: ReviewsSearchBoxProps): JSX.Element => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const [hideResults, setHideResults] = useState(false);
  const query = useAppSelector(state => state.reviewsSearchFilters.interstitial.query);
  const { entityType, ratings, activities, months, query: permanentQuery } = useAppSelector(state => state.reviewsSearchFilters.permanent);
  const filterCount = getReviewFilterCount(entityType, ratings, activities, months);
  const { getValue: getRecentSearches, setValue: setRecentSearches } = useLocalStorage<SuggestionResult[]>(REVIEWS_SEARCH_HISTORY_KEY);
  const recentSearches = getRecentSearches();

  const onSearchCommit: OnSelect<SuggestionResult> = useCallback(
    async suggestion => {
      setHideResults(true);
      const recentSearches = getRecentSearches();
      const searchHistory = [...(recentSearches ?? [])];
      if (suggestion) {
        dispatch(setInterstitialQuery(suggestion.suggestionText));
        onSearch(recentSearches?.length ? 'recent search term' : 'suggested search term');
        setRecentSearches([suggestion, ...searchHistory.filter(({ id }) => id !== suggestion.id)].slice(0, 4));
      } else {
        const trimmedQuery = query?.trim() ?? '';
        if (trimmedQuery !== (permanentQuery ?? '')) {
          onSearch('custom search term');
          if (trimmedQuery) {
            const newSearchItem = { id: `review-search-${trimmedQuery}`, suggestionText: trimmedQuery } as SuggestionResult;
            setRecentSearches([newSearchItem, ...searchHistory.filter(({ id }) => id !== newSearchItem.id)].slice(0, 4));
          }
        }
      }
      await dispatch(saveInterstitial());
      setHideResults(false);
    },
    [dispatch, getRecentSearches, onSearch, permanentQuery, query, setRecentSearches]
  );

  const iconButtonProps = useMemo(
    () => ({
      icon: { Component: ArrowSq, orientation: 'right' as IconProps['orientation'] },
      onClick: () => onSearchCommit(),
      testId: 'search-reviews',
      title: intl.formatMessage({ defaultMessage: 'Search reviews' })
    }),
    [intl, onSearchCommit]
  );

  const results = recentSearches?.length ? recentSearches : getSearchSuggestions(intl);
  return (
    <>
      {/* Static element used to pad sticky elements without visible background */}
      <div className={classNames(styles.container, className)}>
        <FullscreenSearchBox
          clearSelectionOnCommit
          commitOnEmptyQuery
          hideResults={hideResults}
          iconButtonProps={iconButtonProps}
          mobileFocusTrapOptions={{ returnFocusOnDeactivate: false, preventScroll: true }}
          onCommit={onSearchCommit}
          onInputFocus={onSearchFocus}
          onQueryChange={(newQuery: string) => dispatch(setInterstitialQuery(newQuery || ''))}
          placeholder={intl.formatMessage({ defaultMessage: 'Search reviews' })}
          preventScrollOnResultSelection
          query={query ?? ''}
          renderResultContent={({ suggestionText }) => <ReviewSearchSuggestion suggestionText={suggestionText} />}
          results={[
            {
              text: recentSearches?.length
                ? intl.formatMessage({ defaultMessage: 'Recent searches' })
                : intl.formatMessage({ defaultMessage: 'Suggested searches' })
            },
            ...results.map(result => ({ ...result, containerClassName: styles.resultItem }))
          ]}
          tertiaryElement={<FilterButton filterCount={filterCount} onClick={onFilterClick} />}
          variant="default"
        />
      </div>
    </>
  );
};

export default ReviewsSearchBox;
